import React from 'react';
import {View} from 'react-native';
import {noop} from 'lodash';

import Icon from '/component/base/Icon';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import {SearchAndFilter} from '/constant/TestIDs';
import {useTranslation} from '/provider/TranslationProvider';
import testProperties from '/util/testing/testProperties';

import {SearchResults} from '../SearchResults';

import {styles} from './RecentSearches.style';

type RecentSearchesProps = {
  hasRecentSearches: boolean;
  renderItem: () => React.ReactNode;
  deleteAllRecentSearches: () => void;
};

export const RecentSearches: React.FC<RecentSearchesProps> = ({
  hasRecentSearches = false,
  renderItem = noop,
  deleteAllRecentSearches = noop,
}) => {
  const {ts} = useTranslation('screen.search');

  return (
    <>
      {hasRecentSearches ? (
        <>
          <View
            style={styles.headerContainer}
            accessible={true}
            accessibilityLabel="Recently searched profiles"
            accessibilityRole="header">
            <Text
              accessible={true}
              style={styles.headerText}
              variant="subheading"
              testID={SearchAndFilter.recentSearchesTopHeader}>
              {ts('recentlySearched', 'Recently searched profiles')}
            </Text>
            <TouchableOpacity
              accessible={true}
              accessibilityLabel="Clear All"
              style={styles.buttonContainer}
              onPress={deleteAllRecentSearches}
              {...testProperties(null, true)}>
              <Text
                variant="button"
                testID={SearchAndFilter.recentSearchesClearAllButton}>
                {ts('clearAll', 'Clear All')}
              </Text>
            </TouchableOpacity>
          </View>
          <SearchResults renderItem={renderItem} isRecentSearches />
        </>
      ) : (
        <>
          <View
            style={styles.emptyHeaderContainer}
            accessible={true}
            accessibilityLabel="No recent searches"
            accessibilityRole="header">
            <Text
              style={styles.headerText}
              variant="subheading"
              testID={SearchAndFilter.noRecentSearchesTopHeader}>
              {ts('noRecent', 'No recent searches')}
            </Text>
          </View>
          <View
            style={styles.noResultsContainer}
            {...testProperties(null, false)}
            accessible={false}>
            <Icon
              name="slim-search"
              style={styles.icon}
              accessible={false}
              importantForAccessibility="no"
            />
            <Text
              accessible={true}
              accessibilityLabel="No recent searches"
              accessibilityRole="header"
              variant="large"
              style={styles.heading}
              testID={SearchAndFilter.noRecentSearchesBodyHeader}>
              {ts('noRecent', 'No recent searches')}
            </Text>
            <Text
              accessible={true}
              accessibilityLabel="Your recent searches will appear here"
              accessibilityRole="text"
              style={styles.text}
              testID={SearchAndFilter.noRecentSearchesLabel}>
              {ts('willAppearHere', 'Your recent searches will appear here')}
            </Text>
          </View>
        </>
      )}
    </>
  );
};
